// PdfVersionsModal — the More menu's "PDF versions": the version this
// submission reads on now, and the older ones before it, each older one
// downloadable as its stored PDF. Single-submission by design (the meta strip
// is one row's identity), so the menu item is disabled for multi-selections.
//
// Everything shown comes from GET /submission-history/details/:submissionId —
// the modal takes only the submission's id, no display data rides in from the
// list. The response holds ONLY the older versions (newest first), so the
// CURRENT row is synthesized as v{count + 1} ("In use", no download; the live
// render is served by the report overlay/download flows instead). At least one
// entry always comes back in practice — an EMPTY history means a version was
// never created (something went sideways) and renders just the empty message,
// no meta strip and no synthesized row. Every entry repeats the submission's
// identity, so the meta strip reads entry 0; its `linkedType` is the only
// payload that distinguishes inspection from task, which is why the ID & REF
// NO. here can prefix `TK-` correctly while the listing's cell cannot.
//
// The `v2`/`v1` markers are literal values, not copy — no i18n keys.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Chip, Modal } from '@/components/common';
import {
  useGetSubmissionPdfHistoryQuery,
  useLazyGetSubmissionHistoryPdfUrlQuery,
  type SubmissionPdfVersion,
} from '@/api/endpoints/submissionsApi';
import type { ApiErrorShape } from '@/api/types';
import { downloadFileUrl } from '@/utils/downloadUrl';
import { fmtSourceCode } from './submissionColumns';

interface PdfVersionsModalProps {
  /** The single selected submission's id, or null while the modal is closed. */
  submissionId: string | null;
  onClose: () => void;
}

const PdfVersionsModal = ({ submissionId, onClose }: PdfVersionsModalProps) => {
  const { t } = useTranslation();

  const {
    data: versions = [],
    isFetching,
    isError,
  } = useGetSubmissionPdfHistoryQuery(submissionId ?? '', { skip: !submissionId });

  // Older versions only — entry 0 carries the submission's identity for the
  // meta strip, and the current version's number sits one above them all.
  const head = versions[0];
  const currentVersion = versions.length + 1;

  // Download mints a presigned link for THAT version, then saves the bytes —
  // the SubmissionsList downloadPdf idiom, keyed by history entry so only the
  // clicked row's button spins.
  const [fetchUrl] = useLazyGetSubmissionHistoryPdfUrlQuery();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const download = async (v: SubmissionPdfVersion) => {
    setDownloadingId(v.id);
    try {
      const url = await fetchUrl(v.id).unwrap();
      await downloadFileUrl(url, `report-${v.submissionReferenceNumber}-v${v.version}.pdf`);
    } catch (err) {
      // The endpoint's failures carry a server message; downloadFileUrl's throw
      // does not and falls through to the generic copy on purpose.
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Modal
      open={!!submissionId}
      onClose={onClose}
      icon='history'
      title={t('submissions_bulk_pdf_versions')}
      subtitle={t('submissions_versions_subtitle')}
      className='pdf-versions'
      footer={
        <Button variant='secondary' onClick={onClose}>
          {t('button_close')}
        </Button>
      }
    >
      {isFetching ? (
        <p className='pdf-versions__status'>{t('text_loading')}</p>
      ) : isError ? (
        <p className='pdf-versions__status'>{t('error_generic')}</p>
      ) : !head ? (
        <p className='pdf-versions__status'>{t('submissions_versions_empty')}</p>
      ) : (
        <>
          <dl className='pdf-versions__meta'>
            <div className='pdf-versions__meta-item'>
              <dt className='pdf-versions__meta-label'>{t('submissions_versions_form')}</dt>
              <dd className='pdf-versions__meta-value'>{head.formTitle}</dd>
            </div>
            <div className='pdf-versions__meta-item'>
              <dt className='pdf-versions__meta-label'>{t('submissions_col_ref')}</dt>
              <dd className='pdf-versions__meta-value'>
                {fmtSourceCode(head.linkedReferenceNumber, head.linkedType)} ·{' '}
                {head.submissionReferenceNumber}
              </dd>
            </div>
            <div className='pdf-versions__meta-item'>
              <dt className='pdf-versions__meta-label'>{t('submissions_col_by')}</dt>
              <dd className='pdf-versions__meta-value'>{head.submitterName}</dd>
            </div>
          </dl>

          <div className='pdf-versions__card'>
            <div className='pdf-versions__row'>
              <Chip variant='brand-solid' className='pdf-versions__chip'>
                {`v${currentVersion}`}
              </Chip>
              <span className='pdf-versions__row-label'>{t('submissions_versions_current')}</span>
              <span className='pdf-versions__in-use'>{t('submissions_versions_in_use')}</span>
            </div>
            {versions.map((v) => (
              <div key={v.id} className='pdf-versions__row'>
                <Chip variant='outline' className='pdf-versions__chip'>
                  {`v${v.version}`}
                </Chip>
                <span className='pdf-versions__row-label'>{t('submissions_versions_older')}</span>
                <Button
                  variant='secondary'
                  size='sm'
                  iconStart='download'
                  loading={downloadingId === v.id}
                  onClick={() => download(v)}
                >
                  {t('report_pdf_download')}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
};

export default PdfVersionsModal;
